import { ActionContext } from 'vuex';
import { TodoList } from '../../../scripts/api/models/todoList';
import { TodoListItem } from '../../../scripts/api/models/todoListItem';
import { TodoListMember } from '../../../scripts/api/models/todoListMember';

export type TodoListsApiData = {
    lists: Array<TodoList>,
}

export type TodoListApiData = TodoList;

export type TodoListItemApiData = TodoListItem;

export type TodoListMemberApiData = boolean;

export type State = {
    currentTodoList: null | TodoList,
    todoLists: Map<number, TodoList>,
    todoListsState: symbol,
    currentTodoListItems: Array<TodoListItem>,
};

export enum MutationTypes {
    SET_CURRENT_TODO_LIST = 'SET_CURRENT_TODO_LIST',
    TODO_LISTS_FETCHING = 'TODO_LISTS_FETCHING',
    TODO_LISTS_FETCHED = 'TODO_LISTS_FETCHED',
    TODO_LIST_ADDING = 'TODO_LIST_ADDING',
    TODO_LIST_ADDED = 'TODO_LIST_ADDED',
    TODO_LIST_REMOVING = 'TODO_LIST_REMOVING',
    TODO_LIST_REMOVED = 'TODO_LIST_REMOVED',
    TODO_LIST_UPDATING = 'TODO_LIST_UPDATING',
    TODO_LIST_UPDATED = 'TODO_LIST_UPDATED',
    TODO_LIST_ITEM_ADDING = 'TODO_LIST_ITEM_ADDING',
    TODO_LIST_ITEM_ADDED = 'TODO_LIST_ITEM_ADDED',
    TODO_LIST_ITEM_REMOVING = 'TODO_LIST_ITEM_REMOVING',
    TODO_LIST_ITEM_REMOVED = 'TODO_LIST_ITEM_REMOVED',
    TODO_LIST_ITEM_UPDATED = 'TODO_LIST_ITEM_UPDATED',
    TODO_LIST_NEW_LIST_SAVING = 'TODO_LIST_NEW_LIST_SAVING',
    TODO_LIST_NEW_LIST_ITEM_SAVING = 'TODO_LIST_NEW_LIST_ITEM_SAVING',
    TODO_LIST_MEMBER_ADDING = 'TODO_LIST_MEMBER_ADDING',
    TODO_LIST_MEMBER_ADDED = 'TODO_LIST_MEMBER_ADDED',
    TODO_LIST_MEMBER_REMOVING = 'TODO_LIST_MEMBER_REMOVING',
    TODO_LIST_MEMBER_REMOVED = 'TODO_LIST_MEMBER_REMOVED',
}

type MutationsWithoutParameter<S = State> = {
    [MutationTypes.TODO_LISTS_FETCHING](state: S): void
    [MutationTypes.TODO_LIST_ADDING](state: S): void
    [MutationTypes.TODO_LIST_REMOVING](state: S): void
    [MutationTypes.TODO_LIST_UPDATING](state: S): void
    [MutationTypes.TODO_LIST_ITEM_ADDING](state: S): void
    [MutationTypes.TODO_LIST_ITEM_REMOVING](state: S): void
    [MutationTypes.TODO_LIST_NEW_LIST_SAVING](state: S): void
    [MutationTypes.TODO_LIST_NEW_LIST_ITEM_SAVING](state: S): void
    [MutationTypes.TODO_LIST_MEMBER_ADDING](state: S): void
    [MutationTypes.TODO_LIST_MEMBER_REMOVING](state: S): void
}
type MutationsWithOneParameter<S = State> = {
    [MutationTypes.SET_CURRENT_TODO_LIST](state: S, payload: TodoList): void
    [MutationTypes.TODO_LISTS_FETCHED](state: S, data: TodoListsApiData): void
    [MutationTypes.TODO_LIST_ADDED](state: S, data: TodoListApiData): void
    [MutationTypes.TODO_LIST_REMOVED](state: S, todoList: TodoList): void
    [MutationTypes.TODO_LIST_UPDATED](state: S, data: TodoListApiData): void
    [MutationTypes.TODO_LIST_ITEM_ADDED](state: S, data: TodoListItemApiData): void
    [MutationTypes.TODO_LIST_ITEM_REMOVED](state: S, todoListItem: TodoListItem): void
    [MutationTypes.TODO_LIST_ITEM_UPDATED](state: S, data: TodoListItemApiData): void
    [MutationTypes.TODO_LIST_MEMBER_ADDED](state: S, payload: { todoList: TodoList, member: TodoListMember }): void
    [MutationTypes.TODO_LIST_MEMBER_REMOVED](state: S, payload: { todoList: TodoList, member: TodoListMember | null }): void
}
export type Mutations<S = State> = MutationsWithoutParameter<S> & MutationsWithOneParameter<S>;

export type AugmentedActionContext = {
    commit<K extends keyof MutationsWithoutParameter>(
        key: K
    ): ReturnType<MutationsWithoutParameter[K]>
} & {
    commit<K extends keyof MutationsWithOneParameter>(
        key: K,
        payload: Parameters<MutationsWithOneParameter[K]>[1]
    ): ReturnType<MutationsWithOneParameter[K]>
} & Omit<ActionContext<State, State>, 'commit'>;

export interface Actions {
    setCurrentTodoList({ commit } : AugmentedActionContext, todoList: TodoList): void
    fetchTodoLists({ commit, state }: AugmentedActionContext): Promise<void>
    addTodoList({ commit, state }: AugmentedActionContext, todoList: TodoList): void
    removeTodoList({ commit, state }: AugmentedActionContext, todoList: TodoList): Promise<void>
    updateTodoList({ commit, state }: AugmentedActionContext, todoList: TodoList): Promise<void>
    addTodoListItem({ commit, state }: AugmentedActionContext, todoListItem: TodoListItem): void
    removeTodoListItem({ commit, state }: AugmentedActionContext, todoListItem: TodoListItem): Promise<void>
    updateTodoListItem({ commit, state }: AugmentedActionContext, todoListItem: TodoListItem): Promise<void>
    toggleTodoListItem({ commit, state }: AugmentedActionContext, todoListItem: TodoListItem): Promise<void>
    saveNewTodoList({ commit, state }: AugmentedActionContext, todoList: TodoList): Promise<void>
    saveNewTodoListItem({ commit, state }: AugmentedActionContext, todoListItem: TodoListItem): Promise<void>
    addTodoListMember({ commit, state }: AugmentedActionContext, payload: { todoList: TodoList, member: TodoListMember }): Promise<void>
    removeTodoListMember({ commit, state }: AugmentedActionContext, payload: { todoList: TodoList, member: TodoListMember }): Promise<void>
    removeCurrentUserFromList({ commit, state }: AugmentedActionContext, todoList: TodoList): Promise<void>
}

export interface Getters {
    currentTodoList(state: State): null | TodoList;
    todoListById(state: State): (uid: number) => TodoList | undefined;
    todoLists (state: State): Map<number, TodoList>;
    todoListsState (state: State): symbol;
    currentTodoListItems (state: State): Array<TodoListItem>;
}
